<script setup lang="ts">
import { __, p__ } from '@@/bits/intl'
import type { OzBaseDialogBoxButtonData } from '@@/library/v4/components/OzBaseDialogBox.vue'
import OzBaseDialogBox, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzBaseDialogBox.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import isChromatic from 'chromatic/isChromatic'
import { computed, nextTick, ref, watch } from 'vue'

const {
  darkMode = 'auto',
  iconSrc = null,
  body,
  customBodyClasses,
  xShadow = false,
  isCodeProtected = false,
  confirmButtonText,
  cancelButtonText,
  secondaryActionButtonText,
  buttonScheme = OzConfirmationDialogBoxButtonScheme.Default,
  forceFullWidthButtons = false,
  disableConfirmButton = false,
} = defineProps<{
  title: string
  darkMode?: boolean | 'auto'
  iconSrc?: string | null
  body?: string
  customBodyClasses?: string
  xShadow?: boolean
  isCodeProtected?: boolean
  confirmButtonText?: string
  cancelButtonText?: string
  secondaryActionButtonText?: string
  buttonScheme?: OzConfirmationDialogBoxButtonScheme
  forceFullWidthButtons?: boolean
  disableConfirmButton?: boolean
}>()

const emit = defineEmits(['cancel', 'confirm', 'secondary-action'])

// Fix this for Chromatic comparison
const generateCode = (): string =>
  isChromatic()
    ? '0539'
    : Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0')

const generateDifferentCode = (current: string): string => {
  const next = generateCode()
  if (next !== current || isChromatic()) return next
  return ((Number(next) + 1) % 10000).toString().padStart(4, '0')
}

const confirmationCodeInput = ref<InstanceType<typeof OzInput>>()
const codeConfirmationText = ref<string>(__('Enter this code to proceed:'))
const confirmationCode = ref<string>(generateCode())
const enteredCode = ref<string>('')
const isSubmittedCodeValid = ref<boolean>(true)

// TODO: replace current `codeConfirmationText` with this once translations are ready.
const _newCodeConfirmationText = computed<string>(() =>
  p__(
    'confirmationCode is a 4 digit code (e.g 1234).',
    'Enter this code to proceed: <span class="font-semibold">%{confirmationCode}</span>',
    { confirmationCode: confirmationCode.value },
  ),
)

watch(enteredCode, () => {
  if (enteredCode.value.length > 0) {
    isSubmittedCodeValid.value = true
  }
})

const buttons = computed((): OzBaseDialogBoxButtonData[] => {
  const cancelButton = {
    text: cancelButtonText || __('Cancel'),
    emit: 'cancel',
    colorScheme: OzContainedButtonColorScheme.Secondary,
    testId: 'cancelButton',
  }

  const confirmButton = {
    text: confirmButtonText || __('Continue'),
    emit: 'confirm',
    colorScheme: OzContainedButtonColorScheme.Primary,
    disabled: disableConfirmButton || (isCodeProtected && !enteredCode.value.trim()),
    testId: 'confirmButton',
    isFocusedOnLoad: true,
  }

  const secondaryActionButton = {
    text: secondaryActionButtonText as string,
    emit: 'secondary-action',
    colorScheme: OzContainedButtonColorScheme.SecondaryDestructive,
    testId: 'secondaryActionButton',
  }

  // The destructively-styled PRIMARY action. It emits `confirm` (like `confirmButton`) so it triggers
  // the consumer's confirm handler, but is rendered in destructive red to signal a dangerous confirmation.
  const primaryDestructiveButton = {
    text: confirmButtonText || __('Start over'),
    emit: 'confirm',
    colorScheme: OzContainedButtonColorScheme.Destructive,
    testId: 'primaryDestructiveButton',
  }

  // When the buttons are in a row, the prioritized action is right-most.
  // When the buttons are stacked, the prioritized action is top-most.
  let buttonList: OzBaseDialogBoxButtonData[]

  if (buttonScheme === OzConfirmationDialogBoxButtonScheme.Default) {
    buttonList = [cancelButton, confirmButton]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.Danger) {
    buttonList = [cancelButton, { ...confirmButton, colorScheme: OzContainedButtonColorScheme.Destructive }]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.PromoteCancellation) {
    buttonList = [{ ...confirmButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear }, cancelButton]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.PrimarySecondaryDestructiveCancel) {
    buttonList = [
      { ...cancelButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear },
      secondaryActionButton,
      confirmButton,
    ]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.SecondaryDestructiveCancel) {
    buttonList = [{ ...cancelButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear }, secondaryActionButton]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.PrimaryDestructiveSecondaryDestructiveCancel) {
    buttonList = [
      { ...cancelButton, class: 'mt-2' },
      {
        ...secondaryActionButton,
        colorScheme: OzContainedButtonColorScheme.SecondaryDestructive,
      },
      primaryDestructiveButton,
    ]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.ConfirmOnly) {
    buttonList = [confirmButton]
  } else if (buttonScheme === OzConfirmationDialogBoxButtonScheme.PrimaryAndSecondary) {
    const primaryAndSecondaryActionButton = {
      text: secondaryActionButtonText as string,
      emit: 'secondary-action',
      colorScheme: OzContainedButtonColorScheme.Secondary,
      testId: 'secondaryActionButton',
    }
    buttonList = [primaryAndSecondaryActionButton, confirmButton]
  } else {
    buttonList = [cancelButton, confirmButton]
  }

  return forceFullWidthButtons ? buttonList.reverse() : buttonList
})

const ariaDescribedBy = computed(() => {
  const ids: string[] = []
  if (body) ids.push('confirmation-dialog-body')
  if (isCodeProtected) ids.push('code-confirmation-text')
  return ids.join(' ')
})

const focus = (): void => {
  nextTick(() => confirmationCodeInput.value?.focusInput())
}

const submit = (): void => {
  if (!isCodeProtected) {
    emit('confirm')
  } else if (enteredCode.value.trim() === confirmationCode.value) {
    emit('confirm')
  } else {
    isSubmittedCodeValid.value = false
    focus()
    confirmationCode.value = generateDifferentCode(confirmationCode.value)
  }
  enteredCode.value = ''
}

defineExpose({
  focus,
})
</script>

<script lang="ts">
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.types'

export { OzConfirmationDialogBoxButtonScheme }
export default {}
</script>

<template>
  <OzBox
    :color="$slots.subject ? OzBoxColors.Selection : OzBoxColors.None"
    :radius="24"
    :dark-mode="darkMode"
    :class="[
      'break-word-anywhere',
      // Fix the width to allow subject text to be truncated. Same value as OzBaseDialogBox.
      'w-82',
      // TODO: Fix bottom border radius bleed when subject is present
      xShadow && 'shadow-elevation-5',
    ]"
    data-testid="confirmationDialog"
  >
    <!-- Display the subject of the confirmation -->
    <slot name="subject"></slot>

    <OzBaseDialogBox
      :dark-mode="darkMode"
      :buttons="buttons"
      :force-full-width-buttons="forceFullWidthButtons"
      aria-labelledby="confirmation-dialog-title"
      :aria-describedby="ariaDescribedBy"
      @confirm="submit"
      @cancel="$emit('cancel')"
      @secondary-action="$emit('secondary-action')"
    >
      <div class="px-4 py-6 font-sans text-center">
        <img v-if="iconSrc" :src="iconSrc" width="64" height="64" alt="" class="pb-2.5 select-none" />
        <h2 id="confirmation-dialog-title" class="px-4 pb-2.5 font-semibold break-words text-heading-3">{{ title }}</h2>
        <p
          v-if="body"
          id="confirmation-dialog-body"
          :class="['text-body-small', !!customBodyClasses ? customBodyClasses : 'px-8']"
          v-safe-html="body"
        />
        <div v-if="isCodeProtected">
          <div class="mb-6 text-body-small-posts">
            <span id="code-confirmation-text" class="me-1">{{ codeConfirmationText }}</span>
            <span data-pw="confirmationCode" data-testid="confirmationCode" class="font-semibold">{{
              confirmationCode
            }}</span>
          </div>
          <OzInput
            ref="confirmationCodeInput"
            :model-value="enteredCode"
            test-id="confirmationCodeInput"
            type="text"
            placeholder="0000"
            inputmode="numeric"
            pattern="[0-9]*"
            :valid="isSubmittedCodeValid ? 'yes' : 'no'"
            :validation-message="__('Wrong code')"
            :dark-mode="darkMode"
            :aria-label="__('Confirmation code')"
            @update:modelValue="enteredCode = $event"
            @keydown.enter.stop.prevent="submit"
          />
        </div>
      </div>
    </OzBaseDialogBox>
  </OzBox>
</template>
